import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { FormattedMessage } from "react-intl";
import { Grid } from "components/Containers";
import PageTitle from "components/PageTitle";
import { useParamFunctions, useTimeline } from "hooks";
import { Paginate, PAGINATION_TYPE } from "components/Pagination";
import { MessagesNoResults } from "components/Messages";
import { TimelineList, TimelineListControls } from "components/Timeline";
import { TIMELINE_LIST_SORTING_VALUES, SORT } from "constants/index";

const KEYS = {
  filters: "f",
  sorts: "s",
  advancedFilters: "af",
  keyword: "keyword",
  sortKey: "sortKey",
  sortDirection: "sortDirection",
};

/**
 * Reusable Timeline Page Component
 *
 * @param {string} resourceId - ID of the resource (profile, mission role, etc.)
 * @param {Object} user - User data to pass to TimelineList
 * @param {string} pageTitle - Page title for Helmet
 * @param {string} subtitleId - Translation ID for page subtitle
 * @param {string} keyName - Optional key name for useTimeline hook
 * @param {string} resourceKeyName - Optional resource key name for useTimeline hook
 */
const TimelinePage = ({ resourceId, user, pageTitle, subtitleId, keyName, resourceKeyName }) => {
  const { paramParse, setParams } = useParamFunctions();
  const paginationType = PAGINATION_TYPE.classic;
  const prevSelectedRef = useRef();

  // Get initial values from URL parameters
  const initialFilters = paramParse(KEYS.filters);
  const initialSorts = paramParse(KEYS.sorts);
  const initialAdvancedFilters = paramParse(KEYS.advancedFilters);

  const [selected, setSelected] = useState({
    keyword: initialFilters?.[KEYS.keyword] ?? "",
    sortKey: initialSorts?.[KEYS.sortKey] ?? TIMELINE_LIST_SORTING_VALUES.created_at,
    sortDirection: initialSorts?.[KEYS.sortDirection] ?? SORT.desc,
    advancedFilters: initialAdvancedFilters ?? {},
  });

  const {
    dataByDate,
    loading,
    filterMetadata,
    handleNextPage,
    handlePreviousPage,
    hasNextPage,
    hasPreviousPage,
    loadingMore,
    pageInfo,
    currentPage,
    resultsPerPage,
    totalCount,
  } = useTimeline({
    resourceId,
    keyName,
    resourceKeyName,
    advancedFilters: selected?.advancedFilters,
    keyword: selected?.keyword,
    sortKey: selected?.sortKey,
    sortDirection: selected?.sortDirection,
    paginationType,
  });

  const pProps = {
    type: paginationType,
    onNextPage: handleNextPage,
    onPreviousPage: handlePreviousPage,
    hasNextPage: !loading && hasNextPage,
    hasPreviousPage: !loading && hasPreviousPage,
    loading,
    loadingMore,
    pageInfo,
    totalCount,
    currentPage,
    resultsPerPage,
  };

  useEffect(() => {
    // To avoid re-rendering skip if the values are exactly the same as previous
    if (JSON.stringify(prevSelectedRef.current) === JSON.stringify(selected)) return;

    setParams(KEYS.filters, { [KEYS.keyword]: selected.keyword }, true, true);
    setParams(KEYS.advancedFilters, selected.advancedFilters, true, true);
    setParams(
      KEYS.sorts,
      {
        [KEYS.sortKey]: selected.sortKey,
        [KEYS.sortDirection]: selected.sortDirection,
      },
      true,
      true
    );
    if (prevSelectedRef.current) {
      setParams("page", 1, false);
    }

    // Update the ref with current values
    prevSelectedRef.current = selected;
  }, [selected, setParams]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Grid>
        <Grid.col start={1} end={13}>
          <PageTitle subtitle={<FormattedMessage id={subtitleId} />} />
        </Grid.col>
        <Grid.col start={1} end={13}>
          <TimelineListControls
            loading={loading}
            selected={selected}
            setSelected={setSelected}
            formValues={filterMetadata}
          />
        </Grid.col>
        <Grid.col start={1} end={13}>
          {!loading && !totalCount ? (
            <MessagesNoResults />
          ) : (
            <>
              <Paginate {...pProps} isTop />
              <TimelineList id={resourceId} user={user} data={dataByDate} loading={loading} />
              <Paginate {...pProps} />
            </>
          )}
        </Grid.col>
      </Grid>
    </>
  );
};

TimelinePage.propTypes = {
  resourceId: PropTypes.string.isRequired,
  user: PropTypes.object,
  pageTitle: PropTypes.string.isRequired,
  subtitleId: PropTypes.string.isRequired,
  keyName: PropTypes.string,
  resourceKeyName: PropTypes.string,
};

export default TimelinePage;
